import React, { useState, useEffect } from 'react';
import ChatBox from '../../components/ChatBox';
import API from '../../utils/api';
import { SOCKET_URL, getUploadUrl } from '../../utils/config';
import { io } from 'socket.io-client';
import { useAuth } from '../../context/AuthContext';
import { FaSearch, FaUsers, FaUser, FaPhoneAlt } from 'react-icons/fa';

export default function AdminChat() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);

  const fetchMembers = () => {
    setLoading(true);
    API.get('/members?status=approved&limit=500')
      .then(res => {
        const raw = res.data.members || res.data || [];
        const approvedOnly = raw.filter(m => m.status === 'approved');
        setMembers(approvedOnly);
        setSelectedMember(prev => {
          if (!prev) return approvedOnly.length > 0 ? approvedOnly[0] : null;
          if (prev.id === 'all') return prev;
          const found = approvedOnly.find(m => m.id === prev.id || m.member_id === prev.member_id);
          return found || (approvedOnly.length > 0 ? approvedOnly[0] : null);
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const fetchMessages = () => {
    API.get('/messages').then(res => setMessages(res.data || [])).catch(() => {});
  };

  useEffect(() => {
    fetchMembers();
    fetchMessages();

    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socket.on('connect', () => {
      socket.emit('join_room', { userId: 'admin', role: 'admin', name: 'Admin Team' });
    });

    socket.on('receive_message', (msg) => {
      setMessages(prev => {
        const exists = prev.some(m => m.id === msg.id);
        if (exists) return prev;
        return [...prev, msg];
      });
    });

    return () => socket.disconnect();
  }, []);

  // Track read message IDs per member so opened chat does not display message count
  const [readMemberMap, setReadMemberMap] = useState(() => {
    try {
      const saved = localStorage.getItem('admin_read_member_messages');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Whenever a member is opened or messages update, mark that member's messages as read
  useEffect(() => {
    if (!selectedMember) return;
    const mId = String(selectedMember.member_id || selectedMember.id || '');
    const mName = (selectedMember.name || '').toLowerCase();

    const memberMsgs = messages.filter(msg => {
      const sRole = msg.senderRole || msg.sender_role;
      if (sRole !== 'user') return false;
      const sId = String(msg.senderId || msg.sender_id || '');
      const sName = (msg.senderName || msg.sender_name || '').toLowerCase();
      return sId === mId || (sName && sName === mName);
    });

    if (memberMsgs.length > 0) {
      const maxId = Math.max(...memberMsgs.map(m => Number(m.id) || 0));
      setReadMemberMap(prev => {
        if (prev[mId] === maxId) return prev;
        const updated = { ...prev, [mId]: maxId };
        try {
          localStorage.setItem('admin_read_member_messages', JSON.stringify(updated));
        } catch {}
        return updated;
      });
    }
  }, [selectedMember, messages]);

  const getMemberMessageCount = (m) => {
    if (m.id === 'all') return 0;
    // When messages are opened for this member, displaying number should NOT be seen
    if (selectedMember && (selectedMember.id === m.id || selectedMember.member_id === m.member_id)) {
      return 0;
    }
    const mId = String(m.member_id || m.id || '');
    const mName = (m.name || '').toLowerCase();
    const lastReadId = readMemberMap[mId] || 0;

    return messages.filter(msg => {
      const sRole = msg.senderRole || msg.sender_role;
      if (sRole !== 'user') return false;
      const sId = String(msg.senderId || msg.sender_id || '');
      const sName = (msg.senderName || msg.sender_name || '').toLowerCase();
      const isFromMember = sId === mId || (sName && sName === mName);
      return isFromMember && Number(msg.id) > Number(lastReadId);
    }).length;
  };

  const filteredMembers = members.filter(m => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (m.name && m.name.toLowerCase().includes(q)) ||
      (m.member_id && m.member_id.toLowerCase().includes(q)) ||
      (m.mobile_phone && m.mobile_phone.includes(q))
    );
  });

  return (
    <div>
      <div className="topbar">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', fontFamily: 'Poppins, sans-serif' }}>Member Chat</h1>
          <p style={{ fontSize: 13, color: '#6b7280' }}>
            Select an approved member to chat directly, or use the broadcast channel to message all approved members
          </p>
        </div>
      </div>

      <div className="page-content">
        <div className="admin-chat-grid">
          {/* Left Panel: Member Selection Directory */}
          <div className="chat-members-sidebar">
            <div className="chat-members-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, background: '#f0fdf4',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a'
                }}>
                  <FaUsers size={16} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>Directory</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{members.length} Approved Members</div>
                </div>
              </div>
              {isSuperAdmin && (
                <button
                  type="button"
                  id="header-select-all-btn"
                  onClick={() => setSelectedMember({
                    id: 'all',
                    member_id: 'all',
                    name: 'All Members (Everyone)',
                    isAll: true
                  })}
                  title="Select all members to send bulk message"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '5px 10px',
                    fontSize: 11,
                    fontWeight: 700,
                    borderRadius: 6,
                    border: selectedMember?.id === 'all' ? '1px solid #16a34a' : '1px solid #bbf7d0',
                    background: selectedMember?.id === 'all' ? '#16a34a' : '#f0fdf4',
                    color: selectedMember?.id === 'all' ? '#ffffff' : '#15803d',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  📢 Select All
                </button>
              )}
            </div>

            {/* Search Input */}
            <div className="chat-members-search">
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, background: 'white',
                border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px'
              }}>
                <FaSearch size={12} color="#9ca3af" />
                <input
                  type="text"
                  placeholder="Search name, ID, phone..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{
                    border: 'none', outline: 'none', background: 'transparent',
                    width: '100%', fontSize: 12, color: '#111827'
                  }}
                />
              </div>
            </div>

            {/* Registered Members List */}
            <div className="chat-members-list">
              {/* Super Admin: Select All / Bulk Option */}
              {isSuperAdmin && (
                <div style={{ padding: '4px 4px 8px' }}>
                  <button
                    type="button"
                    id="select-all-broadcast-btn"
                    className={`chat-member-item ${selectedMember?.id === 'all' ? 'active' : ''}`}
                    onClick={() => setSelectedMember({
                      id: 'all',
                      member_id: 'all',
                      name: 'All Members (Everyone)',
                      isAll: true
                    })}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: selectedMember?.id === 'all' ? '1px solid #16a34a' : '1px solid #bbf7d0',
                      background: selectedMember?.id === 'all' ? '#ecfdf5' : '#f0fdf4',
                      borderLeft: selectedMember?.id === 'all' ? '4px solid #16a34a' : '4px solid #10b981',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left',
                      outline: 'none',
                      fontFamily: 'inherit',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{
                      width: 38, height: 38, borderRadius: '50%',
                      background: selectedMember?.id === 'all' ? 'linear-gradient(135deg, #15803d, #22c55e)' : '#dcfce7',
                      color: selectedMember?.id === 'all' ? 'white' : '#15803d',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 18, flexShrink: 0,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                    }}>
                      📢
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                      }}>
                        <div style={{
                          fontWeight: 700, fontSize: 13,
                          color: selectedMember?.id === 'all' ? '#15803d' : '#111827'
                        }}>
                          Select All (Everyone)
                        </div>
                        <span style={{
                          background: '#16a34a', color: 'white', fontSize: 10,
                          fontWeight: 700, padding: '1px 6px', borderRadius: 4
                        }}>
                          Bulk
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: '#4b5563', marginTop: 2 }}>
                        Send bulk message to all {members.length} approved members
                      </div>
                    </div>
                  </button>
                </div>
              )}
              <div style={{
                fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase',
                letterSpacing: '0.05em', padding: '10px 6px 4px'
              }}>
                Approved Members ({filteredMembers.length})
              </div>

              {loading && members.length === 0 ? (
                <div style={{ padding: '24px 0', textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  <div className="spinner" style={{ margin: '0 auto 8px', width: 20, height: 20 }}></div>
                  Loading approved members...
                </div>
              ) : filteredMembers.length === 0 ? (
                <div style={{ padding: '24px 12px', textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  {search ? `No approved members found matching "${search}"` : 'No approved members available'}
                </div>
              ) : (
                filteredMembers.map(m => {
                  const isSelected = selectedMember?.id === m.id || selectedMember?.member_id === m.member_id;
                  const initials = m.name ? m.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() : 'M';
                  return (
                    <button
                      type="button"
                      key={m.id || m.member_id}
                      id={`member-item-${m.member_id}`}
                      className={`chat-member-item ${isSelected ? 'active' : ''}`}
                      onClick={() => setSelectedMember(m)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 12px',
                        borderRadius: 10,
                        border: isSelected ? '1px solid #bbf7d0' : '1px solid transparent',
                        background: isSelected ? '#f0fdf4' : 'transparent',
                        borderLeft: isSelected ? '4px solid #16a34a' : '4px solid transparent',
                        cursor: 'pointer',
                        width: '100%',
                        textAlign: 'left',
                        outline: 'none',
                        fontFamily: 'inherit'
                      }}
                    >
                      <div style={{
                        width: 38, height: 38, borderRadius: '50%',
                        background: isSelected ? 'linear-gradient(135deg, #15803d, #22c55e)' : '#ecfdf5',
                        color: isSelected ? 'white' : '#15803d',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: 13, flexShrink: 0, overflow: 'hidden',
                        border: '1px solid #bbf7d0'
                      }}>
                        {m.photo_path ? (
                          <img
                            src={getUploadUrl(m.photo_path)}
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          initials || <FaUser size={13} />
                        )}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontWeight: isSelected ? 700 : 600, fontSize: 13,
                          color: isSelected ? '#15803d' : '#111827',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}>
                          {m.name}
                        </div>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#6b7280', marginTop: 1
                        }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#047857' }}>
                            {m.member_id}
                          </span>
                          {m.mobile_phone && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              • <FaPhoneAlt size={8} /> {m.mobile_phone}
                            </span>
                          )}
                        </div>
                      </div>

                      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                        {m.status && (
                          <span
                            className={`badge ${m.status === 'approved' ? 'badge-success' : 'badge-warning'}`}
                            style={{ fontSize: 9, padding: '2px 6px', textTransform: 'capitalize' }}
                          >
                            {m.status}
                          </span>
                        )}
                        {!isSelected && getMemberMessageCount(m) > 0 && (
                          <span style={{
                            background: '#16a34a', color: 'white', fontSize: 10,
                            fontWeight: 700, borderRadius: 10, padding: '1px 6px', lineHeight: 1.2
                          }} title={`${getMemberMessageCount(m)} unread messages from ${m.name}`}>
                            {getMemberMessageCount(m)}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Right Panel: Chat Conversation Box */}
          <div style={{ minWidth: 0 }}>
            <ChatBox
              activeRecipient={selectedMember}
              onClearRecipient={() => setSelectedMember(null)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
